/**
 * FDI (two-digit) to ISO 3950 (quadrant + position, e.g. UR8, LL1) mapping
 * for normalizing backend tooth_states so the doctor tooth chart (ISO keys) displays correctly.
 *
 * FDI: quadrant 1=upper right, 2=upper left, 3=lower left, 4=lower right; position 1-8.
 * ISO: UR8–UR1, UL1–UL8 (upper); LR8–LR1, LL1–LL8 (lower).
 */

const FDI_TO_ISO: Record<string, string> = {
  "11": "UR1", "12": "UR2", "13": "UR3", "14": "UR4", "15": "UR5", "16": "UR6", "17": "UR7", "18": "UR8",
  "21": "UL1", "22": "UL2", "23": "UL3", "24": "UL4", "25": "UL5", "26": "UL6", "27": "UL7", "28": "UL8",
  "31": "LL1", "32": "LL2", "33": "LL3", "34": "LL4", "35": "LL5", "36": "LL6", "37": "LL7", "38": "LL8",
  "41": "LR1", "42": "LR2", "43": "LR3", "44": "LR4", "45": "LR5", "46": "LR6", "47": "LR7", "48": "LR8",
};

/** Check if a tooth key looks like FDI (two-digit numeric string). */
export function isFdiKey(key: string): boolean {
  return /^[1-4][1-8]$/.test(String(key).trim());
}

/** Convert a single FDI code to ISO (e.g. "18" -> "UR8"). Pass-through if already ISO or unknown. */
export function fdiToIso(toothKey: string): string {
  const k = String(toothKey).trim();
  return FDI_TO_ISO[k] ?? k;
}

/** Normalize tooth_states so keys are ISO. Backend may return FDI keys; chart expects ISO. */
export function normalizeToothStatesToIso(states: Record<string, string>): Record<string, string> {
  if (!states || typeof states !== "object") return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(states)) {
    const isoKey = isFdiKey(key) ? fdiToIso(key) : key;
    out[isoKey] = value;
  }
  return out;
}

/** Normalize a record keyed by tooth (e.g. surfaces, flags, movements) to ISO keys. */
export function normalizeToothRecordToIso<T>(record: Record<string, T>): Record<string, T> {
  if (!record || typeof record !== "object") return {} as Record<string, T>;
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) {
    const isoKey = isFdiKey(key) ? fdiToIso(key) : key;
    out[isoKey] = value;
  }
  return out;
}

/** ISO 3950 to FDI mapping (reverse of FDI_TO_ISO) */
const ISO_TO_FDI: Record<string, string> = {
  "UR1": "11", "UR2": "12", "UR3": "13", "UR4": "14", "UR5": "15", "UR6": "16", "UR7": "17", "UR8": "18",
  "UL1": "21", "UL2": "22", "UL3": "23", "UL4": "24", "UL5": "25", "UL6": "26", "UL7": "27", "UL8": "28",
  "LL1": "31", "LL2": "32", "LL3": "33", "LL4": "34", "LL5": "35", "LL6": "36", "LL7": "37", "LL8": "38",
  "LR1": "41", "LR2": "42", "LR3": "43", "LR4": "44", "LR5": "45", "LR6": "46", "LR7": "47", "LR8": "48",
};

/** Convert ISO notation to FDI (e.g. "UR8" -> "18", "LL2" -> "32"). Pass-through if already FDI or unknown. */
export function isoToFdi(toothKey: string): string {
  const k = String(toothKey).trim().toUpperCase();
  return ISO_TO_FDI[k] ?? k;
}
